// src/services/cartService.ts - VERSION CORRIGÉE avec frais de livraison
import api from './api';
import negotiationService from './negotiationService';

// ✅ MODIFIÉ - Interface CartItem avec delivery_fee
export interface CartItem {
  id: number;
  user_id: number;
  product_id: number;
  quantity: number;
  product: {
    id: number;
    name: string;
    description?: string;
    price: number;
    image_url?: string;
    thumbnail_url?: string;
    stock: number;
    delivery_fee?: number;  // ✅ NOUVEAU CHAMP
    owner_id?: number;      // ✅ AJOUT pour les frais par farmer
  };
  // ✅ NOUVEAU - Prix négocié si applicable
  negotiated_price?: number;
  is_negotiated?: boolean;
}

export interface DeliveryAddress {
  fullName: string;
  phone: string;
  email: string;
  address: string;
  city: string;
  postalCode: string;
  additionalInfo: string;
}

export interface CheckoutRequest {
  delivery_address: DeliveryAddress;
  payment_method: string;
  delivery_instructions?: string;
}

export interface CheckoutResponse {
  id: number;
  total_price: number;
  status: string;
  created_at: string;
  items: any[];
}

class CartService {
  
  // ===== MÉTHODES EXISTANTES (pour compatibilité) =====
  
  // ✅ AJOUT - Méthode manquante pour ProductsPage
  async getAvailableProducts(): Promise<any[]> {
    try {
      console.log('📦 Récupération des produits disponibles...');
      
      // Utiliser l'endpoint existant qui fonctionne
      const response = await api.get('/products/');
      
      console.log('✅ Produits récupérés:', response.data.length);
      return response.data;
      
    } catch (error) {
      console.error('❌ Erreur lors de la récupération des produits:', error);
      throw error;
    }
  }
  
  // ✅ MODIFIÉ - Utiliser l'endpoint customer/cart qui fonctionne
  async getCart(): Promise<CartItem[]> {
    try {
      console.log('🛒 Récupération du panier...');
      
      // Utiliser l'endpoint qui fonctionne selon les logs
      const response = await api.get('/customer/cart');
      let cartItems = response.data;
      
      // ✅ NOUVEAU - Enrichir avec les prix négociés
      cartItems = await this.enrichCartWithNegotiatedPrices(cartItems);
      
      console.log('✅ Panier récupéré avec prix négociés:', cartItems.length, 'articles');
      return cartItems;
      
    } catch (error) {
      console.error('❌ Erreur lors de la récupération du panier:', error);
      throw error;
    }
  }

  // ✅ NOUVEAU - Enrichir le panier avec les prix négociés
  async enrichCartWithNegotiatedPrices(cartItems: CartItem[]): Promise<CartItem[]> {
    try {
      const enrichedItems = await Promise.all(
        cartItems.map(async (item) => {
          try {
            // Vérifier s'il y a un prix négocié pour ce produit
            const negotiatedInfo = await negotiationService.getNegotiatedPrice(item.product_id);
            
            if (negotiatedInfo.has_negotiated_price && negotiatedInfo.negotiated_price) {
              return {
                ...item,
                negotiated_price: negotiatedInfo.negotiated_price,
                is_negotiated: true
              };
            }
            
            return {
              ...item,
              is_negotiated: false
            };
          } catch (error) {
            console.warn(`Erreur lors de la vérification du prix négocié pour le produit ${item.product_id}:`, error);
            return {
              ...item,
              is_negotiated: false
            };
          }
        })
      );
      
      return enrichedItems;
    } catch (error) {
      console.error('Erreur lors de l\'enrichissement avec les prix négociés:', error);
      return cartItems.map(item => ({ ...item, is_negotiated: false }));
    }
  }

  async addToCart(productId: number, quantity: number = 1): Promise<CartItem> {
    try {
      console.log('➕ Ajout au panier:', { productId, quantity });
      
      // Utiliser l'endpoint customer existant qui fonctionne
      const response = await api.post('/customer/cart', {
        product_id: productId,
        quantity: quantity
      });
      
      console.log('✅ Produit ajouté au panier:', response.data);
      return response.data;
      
    } catch (error) {
      console.error('❌ Erreur lors de l\'ajout au panier:', error);
      throw error;
    }
  }

  async updateCartItem(itemId: number, quantity: number): Promise<CartItem> {
    try {
      console.log('🔄 Mise à jour article panier:', { itemId, quantity });
      
      const response = await api.put(`/customer/cart/${itemId}`, {
        quantity: quantity
      });
      
      console.log('✅ Article mis à jour:', response.data);
      return response.data;
      
    } catch (error) {
      console.error('❌ Erreur lors de la mise à jour:', error);
      throw error;
    }
  }

  async removeFromCart(itemId: number): Promise<void> {
    try {
      console.log('🗑️ Suppression article panier:', itemId);
      
      await api.delete(`/customer/cart/${itemId}`);
      
      console.log('✅ Article supprimé du panier');
      
    } catch (error) {
      console.error('❌ Erreur lors de la suppression:', error);
      throw error;
    }
  }

  async clearCart(): Promise<void> {
    try {
      console.log('🧹 Vidage du panier...');
      
      await api.delete('/customer/cart/clear');
      
      console.log('✅ Panier vidé');
      
    } catch (error) {
      console.error('❌ Erreur lors du vidage du panier:', error);
      throw error;
    }
  }

  // ===== CALCULS DU PANIER =====
  
  calculateCartTotal(cartItems: CartItem[]): number {
    return cartItems.reduce((total, item) => {
      // ✅ MODIFIÉ - Utiliser le prix négocié si disponible
      const price = item.is_negotiated && item.negotiated_price 
        ? item.negotiated_price 
        : item.product.price;
      return total + (price * item.quantity);
    }, 0);
  }

  calculateCartItemsCount(cartItems: CartItem[]): number {
    return cartItems.reduce((count, item) => count + item.quantity, 0);
  }

  // ✅ CORRIGÉ - Calculer les vrais frais de livraison par produit
  calculateDeliveryFee(cartItems: CartItem[], city?: string): number {
    console.log('🔍 calculateDeliveryFee called with:', cartItems.length, 'items');
    
    const totalFee = cartItems.reduce((totalFee, item) => {
      const productDeliveryFee = item.product.delivery_fee || 0;
      console.log(`📦 ${item.product.name}: delivery_fee = ${productDeliveryFee}`);
      return totalFee + productDeliveryFee;
    }, 0);
    
    console.log(`💰 Total delivery fee: ${totalFee}`);
    return totalFee;
  }

  calculateFinalTotal(cartItems: CartItem[], deliveryFee: number = 0): number {
    return this.calculateCartTotal(cartItems) + deliveryFee;
  }

  // ✅ NOUVEAU - Calculer les économies réalisées grâce aux négociations
  calculateTotalSavings(cartItems: CartItem[]): number {
    return cartItems.reduce((savings, item) => {
      if (item.is_negotiated && item.negotiated_price) {
        const originalTotal = item.product.price * item.quantity;
        const negotiatedTotal = item.negotiated_price * item.quantity;
        return savings + (originalTotal - negotiatedTotal);
      }
      return savings;
    }, 0);
  }

  // ✅ NOUVEAU - Calculer les frais par farmer
  calculateDeliveryFeeByFarmer(cartItems: CartItem[]): { [farmerId: number]: number } {
    const feesByFarmer: { [farmerId: number]: number } = {};
    
    cartItems.forEach(item => {
      const farmerId = item.product.owner_id || 0;
      const deliveryFee = item.product.delivery_fee || 0;
      
      if (!feesByFarmer[farmerId]) {
        feesByFarmer[farmerId] = 0;
      }
      feesByFarmer[farmerId] += deliveryFee;
    });
    
    return feesByFarmer;
  }

  // ✅ NOUVEAU - Détail des frais de livraison
  getDeliveryFeeBreakdown(cartItems: CartItem[]): {
    totalFee: number;
    freeDeliveryItems: number;
    paidDeliveryItems: number;
    breakdown: Array<{
      productName: string;
      fee: number;
      isFree: boolean;
      quantity: number;
    }>;
  } {
    const breakdown = cartItems.map(item => ({
      productName: item.product.name,
      fee: item.product.delivery_fee || 0,
      isFree: (item.product.delivery_fee || 0) === 0,
      quantity: item.quantity
    }));
    
    const totalFee = this.calculateDeliveryFee(cartItems);
    const freeDeliveryItems = breakdown.filter(item => item.isFree).length;
    const paidDeliveryItems = breakdown.filter(item => !item.isFree).length;
    
    return {
      totalFee,
      freeDeliveryItems,
      paidDeliveryItems,
      breakdown
    };
  }

  // ✅ NOUVEAU - Calculer le total avec détail complet
  getDetailedCartTotal(cartItems: CartItem[]): {
    subtotal: number;
    deliveryFee: number;
    savings: number;
    finalTotal: number;
    breakdown: {
      productsWithFreeDelivery: number;
      productsWithPaidDelivery: number;
      negotiatedItems: number;
    };
  } {
    const subtotal = this.calculateCartTotal(cartItems);
    const deliveryFee = this.calculateDeliveryFee(cartItems);
    const savings = this.calculateTotalSavings(cartItems);
    const finalTotal = subtotal + deliveryFee;
    
    const deliveryBreakdown = this.getDeliveryFeeBreakdown(cartItems);
    
    return {
      subtotal,
      deliveryFee,
      savings,
      finalTotal,
      breakdown: {
        productsWithFreeDelivery: deliveryBreakdown.freeDeliveryItems,
        productsWithPaidDelivery: deliveryBreakdown.paidDeliveryItems,
        negotiatedItems: this.getNegotiatedItemsCount(cartItems)
      }
    };
  }

  // ===== CHECKOUT =====
  
  async checkoutWithDetails(checkoutData: CheckoutRequest): Promise<CheckoutResponse> {
    try {
      console.log('🛒 Processus de checkout avec détails...');
      
      const response = await api.post('/customer/checkout-detailed', checkoutData);
      
      console.log('✅ Checkout réussi:', response.data);
      return response.data;
      
    } catch (error) {
      console.error('❌ Erreur lors du checkout:', error);
      throw error;
    }
  }

  async simpleCheckout(): Promise<CheckoutResponse> {
    try {
      console.log('🛒 Checkout simple...');
      
      const response = await api.post('/customer/checkout');
      
      console.log('✅ Checkout simple réussi:', response.data);
      return response.data;
      
    } catch (error) {
      console.error('❌ Erreur lors du checkout simple:', error);
      throw error;
    }
  }

  // ===== VALIDATIONS =====
  
  validateAddToCart(quantity: number, stock: number): string[] {
    const errors: string[] = [];
    
    if (!quantity || quantity <= 0) {
      errors.push('La quantité doit être supérieure à 0');
    }
    
    if (quantity > stock) {
      errors.push(`Stock insuffisant. Disponible: ${stock}`);
    }
    
    return errors;
  }

  validateDeliveryAddress(address: DeliveryAddress): string[] {
    const errors: string[] = [];
    
    if (!address.fullName || address.fullName.trim().length < 2) {
      errors.push('Le nom complet est requis (minimum 2 caractères)');
    }
    
    if (!address.phone || address.phone.trim().length < 8) {
      errors.push('Le numéro de téléphone est requis');
    }
    
    if (!address.email || !this.isValidEmail(address.email)) {
      errors.push('Une adresse email valide est requise');
    }
    
    if (!address.address || address.address.trim().length < 10) {
      errors.push('L\'adresse complète est requise (minimum 10 caractères)');
    }
    
    if (!address.city || address.city.trim().length < 2) {
      errors.push('La ville est requise');
    }
    
    return errors;
  }

  canCheckout(cartItems: CartItem[]): { canCheckout: boolean; errors: string[] } {
    const errors: string[] = [];
    
    if (cartItems.length === 0) {
      errors.push('Votre panier est vide');
    }
    
    // Vérifier le stock pour chaque article
    cartItems.forEach(item => {
      if (item.quantity > item.product.stock) {
        errors.push(`Stock insuffisant pour ${item.product.name}. Disponible: ${item.product.stock}`);
      }
    });
    
    return {
      canCheckout: errors.length === 0,
      errors
    };
  }

  // ===== UTILITAIRES =====
  
  isProductInCart(cartItems: CartItem[], productId: number): boolean {
    return cartItems.some(item => item.product.id === productId);
  }

  getCartItemForProduct(cartItems: CartItem[], productId: number): CartItem | undefined {
    return cartItems.find(item => item.product.id === productId);
  }

  formatPrice(price: number): string {
    return `${price.toLocaleString()} FCFA`;
  }

  // ✅ NOUVEAU - Formater les frais de livraison
  formatDeliveryFee(fee: number): string {
    return fee > 0 ? `${fee.toLocaleString()} FCFA` : 'Gratuite';
  }

  getPaymentMethodLabel(method: string): string {
    const labels = {
      'mobile_money': 'Mobile Money (MTN/Moov)',
      'cash': 'Paiement à la livraison',
      'card': 'Carte bancaire'
    };
    return labels[method as keyof typeof labels] || method;
  }

  // ===== SAUVEGARDE LOCAL =====
  
  saveCheckoutData(data: CheckoutRequest): void {
    try {
      localStorage.setItem('checkout_data', JSON.stringify(data));
    } catch (error) {
      console.warn('Impossible de sauvegarder les données de checkout:', error);
    }
  }

  getCheckoutData(): CheckoutRequest | null {
    try {
      const data = localStorage.getItem('checkout_data');
      return data ? JSON.parse(data) : null;
    } catch (error) {
      console.warn('Impossible de récupérer les données de checkout:', error);
      return null;
    }
  }

  clearCheckoutData(): void {
    try {
      localStorage.removeItem('checkout_data');
    } catch (error) {
      console.warn('Impossible de supprimer les données de checkout:', error);
    }
  }

  // ===== HELPERS PRIVÉS =====
  
  private isValidEmail(email: string): boolean {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  }

  // ✅ NOUVEAU - Méthodes pour les prix négociés
  
  getEffectivePrice(item: CartItem): number {
    return item.is_negotiated && item.negotiated_price 
      ? item.negotiated_price 
      : item.product.price;
  }

  getItemSavings(item: CartItem): number {
    if (item.is_negotiated && item.negotiated_price) {
      return (item.product.price - item.negotiated_price) * item.quantity;
    }
    return 0;
  }

  hasNegotiatedItems(cartItems: CartItem[]): boolean {
    return cartItems.some(item => item.is_negotiated);
  }

  getNegotiatedItemsCount(cartItems: CartItem[]): number {
    return cartItems.filter(item => item.is_negotiated).length;
  }

  // ✅ NOUVEAU - Résumé du panier avec négociations et frais de livraison
  getCartSummary(cartItems: CartItem[]): {
    itemsCount: number;
    totalOriginal: number;
    totalNegotiated: number;
    totalSavings: number;
    deliveryFee: number;
    finalTotal: number;
    hasNegotiations: boolean;
    negotiatedItemsCount: number;
    freeDeliveryItems: number;
    paidDeliveryItems: number;
  } {
    const itemsCount = this.calculateCartItemsCount(cartItems);
    const totalOriginal = cartItems.reduce((total, item) => total + (item.product.price * item.quantity), 0);
    const totalNegotiated = this.calculateCartTotal(cartItems);
    const totalSavings = this.calculateTotalSavings(cartItems);
    const deliveryFee = this.calculateDeliveryFee(cartItems);
    const finalTotal = totalNegotiated + deliveryFee;
    const hasNegotiations = this.hasNegotiatedItems(cartItems);
    const negotiatedItemsCount = this.getNegotiatedItemsCount(cartItems);
    
    const deliveryBreakdown = this.getDeliveryFeeBreakdown(cartItems);

    console.log('🛒 Cart Summary:', {
      totalNegotiated,
      deliveryFee,
      finalTotal
    });

    return {
      itemsCount,
      totalOriginal,
      totalNegotiated,
      totalSavings,
      deliveryFee,
      finalTotal,
      hasNegotiations,
      negotiatedItemsCount,
      freeDeliveryItems: deliveryBreakdown.freeDeliveryItems,
      paidDeliveryItems: deliveryBreakdown.paidDeliveryItems
    };
  }

  // ✅ NOUVEAU - Obtenir le coût total d'un item (prix + livraison)
  // ✅ CORRECTION - Dans cartService.ts
  getItemTotalCost(item: CartItem): {
    unitPrice: number;
    deliveryFee: number;
    unitTotal: number;
    totalForQuantity: number;
  } {
    const unitPrice = this.getEffectivePrice(item);
    const deliveryFee = item.product.delivery_fee || 0;
    const unitTotal = unitPrice + deliveryFee;
    
    // ✅ CORRECTION - Les frais de livraison ne sont PAS multipliés par la quantité
    const totalForQuantity = (unitPrice * item.quantity) + deliveryFee;

    return {
      unitPrice,
      deliveryFee,
      unitTotal,
      totalForQuantity
    };
  }

  // ✅ NOUVEAU - Grouper les articles par type de livraison
  groupItemsByDeliveryType(cartItems: CartItem[]): {
    freeDelivery: CartItem[];
    paidDelivery: CartItem[];
  } {
    const freeDelivery = cartItems.filter(item => (item.product.delivery_fee || 0) === 0);
    const paidDelivery = cartItems.filter(item => (item.product.delivery_fee || 0) > 0);

    return { freeDelivery, paidDelivery };
  }

  // ✅ NOUVEAU - Vérifier si le panier a des frais de livraison
  hasDeliveryFees(cartItems: CartItem[]): boolean {
    return cartItems.some(item => (item.product.delivery_fee || 0) > 0);
  }

  // ✅ NOUVEAU - Statistiques des frais de livraison
  getDeliveryStats(cartItems: CartItem[]): {
    averageDeliveryFee: number;
    maxDeliveryFee: number;
    minDeliveryFee: number;
    totalDeliveryFee: number;
    itemsWithDeliveryFee: number;
  } {
    const deliveryFees = cartItems.map(item => item.product.delivery_fee || 0);
    const nonZeroFees = deliveryFees.filter(fee => fee > 0);
    
    return {
      averageDeliveryFee: nonZeroFees.length > 0 ? nonZeroFees.reduce((a, b) => a + b, 0) / nonZeroFees.length : 0,
      maxDeliveryFee: Math.max(...deliveryFees),
      minDeliveryFee: Math.min(...deliveryFees),
      totalDeliveryFee: this.calculateDeliveryFee(cartItems),
      itemsWithDeliveryFee: nonZeroFees.length
    };
  }
}

export default new CartService();